import { makeAutoObservable } from "mobx";
import AdminMigrateGlobalEMerchantsService, {
    IGlobaleStoreMetaFields,
    ILastCompletedShopMigrationAttempt,
} from "../../../../services/api/admin-migrate-global-e-merchants-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import toastMessageListStore from "../../../../components/toast-messages-modal/toast-messages-store";
import { IShop } from "../../../../models/User";
import { isSet } from "../../../../models/model-types-methods";


class MigrateGlobaleShopifyPlatformStore {
    private _selectedShops: IShop[] = [];
    private _isProcessing: boolean = false;
    private _isFetchingMetaFields: boolean = false;
    private _globaleMetaFields?: IGlobaleStoreMetaFields;
    private _lastCompletedMigrationAttempt: ILastCompletedShopMigrationAttempt | null = null;
    
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
        this.reset();
    }

    private get service(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    public get isProcessing(): boolean { return this._isProcessing; }
    public get portal() { return this._selectedShops; }
    public get env() { return this._globaleMetaFields?.env; }
    public get gobaleMerchantId() { return this._globaleMetaFields?.merchant_id; }
    public get lastCompletedMigrationAttempt(): ILastCompletedShopMigrationAttempt | null {
        return this._lastCompletedMigrationAttempt;
    }
    public get isValid(): boolean {
        return isSet(this._selectedShops[0]) && isSet(this._globaleMetaFields);
    }

    public setPortal(shops: IShop[]) {
        this._selectedShops = shops;
        this.fetchGlobaleStoreMetaFields();
    }

    private reset() {
        this._globaleMetaFields = undefined;
        this._lastCompletedMigrationAttempt = null;
        this._selectedShops = [];
    }

    public async submit(): Promise<void> {
        if (!this.isValid || this._isProcessing) {
            return;
        }
        
        try {
            this._isProcessing = true;
            await this.service.migrateGlobalEShopifyPlatformStore({ portal_id: this._selectedShops[0].id });
            toastMessageListStore.add({
                title: `Success`,
                description: `Portal id:${this._selectedShops[0].id} migrated and assigned global-e merchant id:${this._globaleMetaFields?.merchant_id}|${this._globaleMetaFields?.env} successfully`,
            });
            this.reset();
        }
        catch (e) {
            console.error(e);
        }
        finally {
            this._isProcessing = false;
        }
    }

    private async fetchGlobaleStoreMetaFields(): Promise<void> {
        const selectedShop = this._selectedShops[0];
        if (!isSet(selectedShop)) {
            this._globaleMetaFields = undefined;
            this._lastCompletedMigrationAttempt = null;
            return;
        }
        try {
            this._isFetchingMetaFields = true;
            this._lastCompletedMigrationAttempt = null;
            const [metaFields, lastAttempt] = await Promise.all([
                this.service.fetchGlobaleStoreMetaFields(selectedShop.id).catch((e) => {
                    console.error(e);
                    return undefined;
                }),
                this.service.fetchLastCompletedShopMigrationAttempt(selectedShop.id).catch((e) => {
                    console.error(e);
                    return null;
                }),
            ]);
            this._globaleMetaFields = metaFields;
            this._lastCompletedMigrationAttempt = lastAttempt;
        }
        finally {
            this._isFetchingMetaFields = false;
        }
    }
}

export default new MigrateGlobaleShopifyPlatformStore();
